import React, {
  useState,
  useEffect,
  useRef
} from 'react';
import ReactDOM from 'react-dom';
import {
  Editor
} from 'slate';
import {
  useSlateStatic,
  ReactEditor
} from 'slate-react';

import HelptapEditor from '../../../helpers';

import fileTypes from 'constants/file-types';

import MediaEditControls from '../media-edit-controls/react';
import AlignmentControls from '../media-edit-controls/alignment-controls';

import DocumentDisplay from './document';

import {
  getStyles,
  getFileContainerClassName,
  determineFileAndType
} from './utilities';

import './index.scss';

function FileDisplay(props) {

  const element = props.element;

  const editor = useSlateStatic();

  const fullscreenContainerReference = useRef(null);
  const fileDisplayMainReference = useRef(null);
  const fileLoaded = useRef(false);
  const mediaControlHideTimerReference = useRef(null);

  const [rootState, setRootState] = useState({
    ...determineFileAndType(props.attributes),
    loading: getDefaultLoadingState(),
    displayImageInFullscreen: false,
    displayControls: false,

  });

  useEffect(function onFileChange() {

    setRootState(function updateRootState(rootState) {
      return {
        ...rootState,
        ...determineFileAndType(props.attributes)
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.attributes.file, props.attributes.fileType]);

  function setDisplayControls(displayControls) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        displayControls
      };

    });

  }

  function hideDisplayControls() {

    mediaControlHideTimerReference.current = setTimeout(function someTimeLater() {
      setDisplayControls(false);
    }, 400);

  }

  function showDisplayControls() {

    clearTimeout(mediaControlHideTimerReference.current);

    setDisplayControls(true);

  }

  function getDefaultLoadingState() {

    if (props.uploading === true) {
      return false;
    }

    return determineFileAndType(props.attributes).fileType !== fileTypes.DOCUMENT;

  }

  function handleFileLoadedEvent() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        loading: false
      };

    });

  }

  function handleLoad() {

    if (fileLoaded.current === false) {
      fileLoaded.current = true;
      handleFileLoadedEvent();
    }

  }

  function setImageFilterProperty(type, value) {

    const path = ReactEditor.findPath(editor, element);
    const node = Editor.node(editor, path)[0];
    HelptapEditor.setAttributes(editor, {
      attributes: {
        ...node.attributes,
        [type]: value
      }
    }, path);
  }

  function resetAllImageFilters() {

    const filterProperties = [
      'filterType',
      'brightness',
      'contrast',
      'saturation',
      'hue',
      'blur',
      'opacity'
    ];

    const newAttributes = {
      ...props.attributes
    };

    filterProperties.forEach(function resetFilterProperty(property) {
      newAttributes[property] = void 0;
    });

    HelptapEditor.setAttributes(editor, {
      attributes: newAttributes
    }, ReactEditor.findPath(editor, element));

  }

  function tryRenderMediaEditControls() {

    if (props.uploading === true || rootState.loading === true) {
      return;
    }

    if (rootState.displayControls === false) {
      return;
    }

    const properties = {
      readOnly: props.attributes.readOnly,
      mediaType: rootState.fileType,
      altText: element.attributes.altText,
      url: rootState.file.url,
      alignment: AlignmentControls.determineAlignment(element),
      anchorReference: fileDisplayMainReference,
      editorMainReference: props.editorMainReference,
      onAlignmentChange(values) {
        HelptapEditor.setAttributes(editor, { ...values }, ReactEditor.findPath(editor, element));
      },
      onAltTextChange(altText) {
        HelptapEditor.setAttributes(editor, {
          attributes: {
            ...props.attributes,
            altText
          }
        }, ReactEditor.findPath(editor, element));
      },
      onURLChange(url) {
        props.handleURLChange(url);
      },
      onFile(file) {
        props.handleRawFile(file);
      },
      clip: element.attributes.clip,
      onClipChange(clip) {
        HelptapEditor.setAttributes(editor, {
          attributes: {
            ...props.attributes,
            clip
          }
        }, ReactEditor.findPath(editor, element));
      },
      fit: element.attributes.fit,
      onFitChange(fit) {
        HelptapEditor.setAttributes(editor, {
          attributes: {
            ...props.attributes,
            fit
          }
        }, ReactEditor.findPath(editor, element));
      },
      filter: {
        filterType: element.attributes.filterType,
        brightness: element.attributes.brightness,
        contrast: element.attributes.contrast,
        saturation: element.attributes.saturation,
        hue: element.attributes.hue,
        blur: element.attributes.blur,
        opacity: element.attributes.opacity,
        onTypeChange: (filterType, active) => {
          setImageFilterProperty('filterType', active ? filterType : null);
        },
        onBrightnessChange: (brightness) => {
          setImageFilterProperty('brightness', brightness);
        },
        onContrastChange: (contrast) => {
          setImageFilterProperty('contrast', contrast);
        },
        onSaturationChange: (saturation) => {
          setImageFilterProperty('saturation', saturation);
        },
        onHueChange: (hue) => {
          setImageFilterProperty('hue', hue);
        },
        onBlurChange: (blur) => {
          setImageFilterProperty('blur', blur);
        },
        onOpacityChange: (opacity) => {
          setImageFilterProperty('opacity', opacity);
        },
        onPixelationChange: (pixelation) => {},
        onRemoveAllFilters: () => {
          resetAllImageFilters();
        }
      },
      onActive() {},
      onInactive() {}
    };

    return <MediaEditControls {...properties} />;

  }

  function tryRenderUploadingProgressDetails() {

    if (props.uploading !== true) {
      return;
    }

    return <div className="uploading-file-display-content">
      <div className='uploading-progress-content'>
        <ht-spinner size="small" />
        <label className='uploading-label'>{props.uploadedPercent}</label>
      </div>
    </div>;

  }

  function tryRenderFileLoadingIndicator() {

    if (rootState.loading === false) {
      return;
    }

    return <div className="loading-indicator-continer">
      <ht-spinner size="small" />
    </div>;

  }

  function renderImageInFullscreen() {

    if (rootState.displayImageInFullscreen === false) {
      return;
    }

    const fullscreenContainerAttributes = {
      className: 'fullscreen-image-container',
      ref: fullscreenContainerReference,
      onClick: function handleFullscreenContainerClick(event) {

        if (event.target !== fullscreenContainerReference.current) {
          return;
        }

        setRootState(function updateRootState(rootState) {
          return {
            ...rootState,
            displayImageInFullscreen: false
          };
        });

      }
    };

    const fullscreenImageAttributes = {
      src: rootState.file.url,
      className: 'image-file'
    };

    const overlay = <div {...fullscreenContainerAttributes}>
      <img {...fullscreenImageAttributes} alt='file-display' />
    </div>;

    return ReactDOM.createPortal(overlay, document.body);

  }

  function renderFileContent() {

    if (rootState.fileType === fileTypes.DOCUMENT) {

      const documentDisplayProperties = {
        file: rootState.file,
        uploading: props.uploading,
        uploadedPercent: props.uploadedPercent
      };

      return <DocumentDisplay {...documentDisplayProperties} />;

    }

    const style = getStyles(props.element);
    const fileAttributes = {
      src: rootState.file.url,
      className: 'image-file',
      style,
      onLoad: handleLoad,
      readOnly: true,
      onClick(event) {
        event.preventDefault();
        event.stopPropagation();
      },
      onDoubleClick() {
        setRootState(function updateRootState(rootState) {
          return {
            ...rootState,
            displayImageInFullscreen: true
          };
        });
      },
      ref: (img) => {

        if (!img) {
          return;
        }

        if (img.complete && fileLoaded.current === false) {
          fileLoaded.current = true;
          handleFileLoadedEvent();
        }
      }
    };

    if (rootState.fileType === fileTypes.IMAGE) {

      fileAttributes.className = 'image-file';

      const containerAttributes = {
        className: getFileContainerClassName(props.element)
      };

      return <div {...containerAttributes}>
        {tryRenderFileLoadingIndicator()}
        {tryRenderUploadingProgressDetails()}
        <img {...fileAttributes} alt='file-display' />
      </div>;

    }

    fileAttributes.onLoadedData = handleFileLoadedEvent;
    fileAttributes.onLoadedMetadata = handleFileLoadedEvent;

    fileAttributes.className = 'video-file';

    return <div className={getFileContainerClassName(props.element)}>
      {tryRenderFileLoadingIndicator()}
      {tryRenderUploadingProgressDetails()}
      <video {...fileAttributes} controls></video>
    </div>;
  }

  function renderMain() {

    const mainAttributes = {
      className: 'file-display-main',
      ref: fileDisplayMainReference,
      onMouseEnter() {
        showDisplayControls(true);
      },
      onMouseLeave() {
        hideDisplayControls(false);
      }
    };

    if (rootState.loading === true || props.uploading === true) {
      mainAttributes.className += ' minimum-height-applied';
    }

    return <div {...mainAttributes}>
      {tryRenderMediaEditControls()}
      {renderFileContent()}
      {renderImageInFullscreen()}
    </div>;

  }

  return renderMain();
}

export default FileDisplay;